// Notification engine — deterministic rule-based generator.
// Stable IDs ensure the same condition does not create duplicates across runs.

import { flattenSubs } from "./programStorage";
import { computeHighlight } from "./progressUtils";
import { hasRole } from "./auth";

export const NOTIF_TYPES = {
  PERINGATAN: "PERINGATAN",
  TERLAMBAT: "TERLAMBAT",
  STAGNASI: "STAGNASI",
  ESKALASI_AUDIT: "ESKALASI_AUDIT",
};

export const NOTIF_LABELS = {
  PERINGATAN: "Peringatan",
  TERLAMBAT: "Terlambat",
  STAGNASI: "Stagnasi",
  ESKALASI_AUDIT: "Eskalasi Audit",
};

function daysBetween(aIso, bDate) {
  if (!aIso) return Infinity;
  const a = new Date(aIso);
  if (Number.isNaN(a.getTime())) return Infinity;
  return (bDate.getTime() - a.getTime()) / (1000 * 60 * 60 * 24);
}

/**
 * Generate notifications from current programs + assignments state.
 * Returns an array of notification objects. IDs are stable (type-subId).
 */
export function runEngine(programs, assignments) {
  const now = new Date();
  const nowIso = now.toISOString();
  const flat = flattenSubs(programs);
  const out = [];

  for (const s of flat) {
    const scope = assignments[s.id] || {};
    const commonCtx = {
      subId: s.id,
      programKode: s.programKode,
      subNama: s.nama,
      scope,
      createdAt: nowIso,
      read: false,
    };

    if (s.specialType === "AUDIT_INVESTIGASI") {
      const overdue =
        s.targetSelesai &&
        new Date(s.targetSelesai) < now &&
        s.status !== "SELESAI";
      if (s.status === "TERHAMBAT" || overdue) {
        out.push({
          id: `ESKALASI_AUDIT-${s.id}`,
          type: NOTIF_TYPES.ESKALASI_AUDIT,
          message: `Eskalasi Audit Investigasi — ${s.nama} memerlukan perhatian Irban.`,
          ...commonCtx,
        });
      }
      continue;
    }

    if (s.status === "SELESAI") continue;

    const highlight = computeHighlight(s);

    if (highlight.level === "RED") {
      out.push({
        id: `TERLAMBAT-${s.id}`,
        type: NOTIF_TYPES.TERLAMBAT,
        message: `${s.nama} sudah lewat deadline (${highlight.label}); progres ${s.progress || 0}%.`,
        ...commonCtx,
      });
    } else if (highlight.level === "YELLOW") {
      out.push({
        id: `PERINGATAN-${s.id}`,
        type: NOTIF_TYPES.PERINGATAN,
        message: `${s.nama} akan jatuh tempo (${highlight.label}); progres ${s.progress || 0}%.`,
        ...commonCtx,
      });
    }

    // STAGNASI: TERHAMBAT + last update > 14 days
    if (s.status === "TERHAMBAT") {
      const last = Array.isArray(s.history) && s.history.length > 0 ? s.history[0].timestamp : null;
      const days = daysBetween(last, now);
      if (days > 14) {
        out.push({
          id: `STAGNASI-${s.id}`,
          type: NOTIF_TYPES.STAGNASI,
          message: `${s.nama} berstatus Terhambat dan tidak ada update ${
            last ? `${Math.floor(days)} hari` : "sejak awal"
          }.`,
          ...commonCtx,
        });
      }
    }
  }

  // Sort: TERLAMBAT → ESKALASI → STAGNASI → PERINGATAN; then by programKode
  const priority = {
    TERLAMBAT: 0,
    ESKALASI_AUDIT: 1,
    STAGNASI: 2,
    PERINGATAN: 3,
  };
  out.sort((a, b) => {
    const d = priority[a.type] - priority[b.type];
    if (d !== 0) return d;
    return a.programKode.localeCompare(b.programKode);
  });

  return out;
}

/**
 * Merge fresh generated notifs with stored ones, preserving `read` state and original createdAt.
 * Drops stored notifs that no longer match a fresh generated id (condition resolved).
 */
export function mergeWithStored(fresh, stored = []) {
  const storedMap = new Map();
  for (const n of stored) storedMap.set(n.id, n);
  return fresh.map((n) => {
    const prev = storedMap.get(n.id);
    if (prev) {
      return { ...n, read: !!prev.read, createdAt: prev.createdAt };
    }
    return n;
  });
}

/**
 * Filter notifications by role-based scope.
 * IRBAN/ADMIN → all; DALNIS → dalnisId match; KETUA_TIM → ketuaId match;
 * ANGGOTA_TIM → in anggotaIds. ESKALASI_AUDIT hanya untuk IRBAN/ADMIN.
 */
export function scopeNotifsForUser(user, notifications) {
  if (!user || !Array.isArray(notifications)) return [];
  if (hasRole(user, "IRBAN")) return notifications;
  return notifications.filter((n) => {
    if (n.type === NOTIF_TYPES.ESKALASI_AUDIT) return false;
    const scope = n.scope || {};
    switch (user.role) {
      case "DALNIS":
        return scope.dalnisId === user.id;
      case "KETUA_TIM":
        return scope.ketuaId === user.id;
      case "ANGGOTA_TIM":
        return Array.isArray(scope.anggotaIds) && scope.anggotaIds.includes(user.id);
      default:
        return false;
    }
  });
}
